/**
 * StatusTimeline Component
 * Visual timeline showing lab order status progression
 */

import React from 'react';
import {
  CheckCircle2,
  Clock,
  Circle,
  FileText,
  Calendar,
  FlaskConical,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface StatusTimelineProps {
  status: string;
}

type StatusStep = {
  key: string;
  label: string;
  icon: React.ReactNode;
};

const STATUS_STEPS: StatusStep[] = [
  { key: 'pending', label: 'Order Created', icon: <CheckCircle2 className="h-4 w-4" /> },
  { key: 'pending_appointment', label: 'Appointment Needed', icon: <Clock className="h-4 w-4" /> },
  { key: 'requisition_ready', label: 'Requisition Ready', icon: <FileText className="h-4 w-4" /> },
  { key: 'appointment_scheduled', label: 'Appointment Scheduled', icon: <Calendar className="h-4 w-4" /> },
  { key: 'sample_collected', label: 'Sample Collected', icon: <FlaskConical className="h-4 w-4" /> },
  { key: 'processing', label: 'Processing', icon: <Clock className="h-4 w-4" /> },
  { key: 'results_ready', label: 'Results Available', icon: <CheckCircle2 className="h-4 w-4" /> },
  { key: 'completed', label: 'Completed', icon: <CheckCircle2 className="h-4 w-4" /> },
];

const StatusTimeline: React.FC<StatusTimelineProps> = ({ status }) => {
  const getStatusIndex = (status: string): number => {
    const statusMap: Record<string, number> = {
      pending: 0,
      pending_appointment: 1,
      requisition_ready: 1,
      appointment_scheduled: 3,
      sample_collected: 4,
      processing: 5,
      results_ready: 6,
      completed: 7,
      reviewed: 7,
    };
    return statusMap[status] ?? 0;
  };

  const currentIndex = getStatusIndex(status);

  return (
    <div className="flex items-center gap-2">
      {STATUS_STEPS.slice(0, 4).map((step, index) => {
        const isCompleted = index <= currentIndex;
        const isCurrent = index === currentIndex;

        return (
          <React.Fragment key={step.key}>
            <div
              className={cn(
                'flex flex-col items-center gap-1',
                isCompleted ? 'text-blue-400' : 'text-slate-500'
              )}
            >
              <div
                className={cn(
                  'rounded-full p-1.5',
                  isCompleted
                    ? 'bg-blue-500 text-white'
                    : 'bg-slate-700 text-slate-400'
                )}
              >
                {isCompleted ? (
                  <CheckCircle2 className="h-3 w-3" />
                ) : (
                  <Circle className="h-3 w-3" />
                )}
              </div>
              <span className="text-xs text-center max-w-[60px]">
                {step.label}
              </span>
            </div>
            {index < 3 && (
              <div
                className={cn(
                  'h-0.5 w-8',
                  index < currentIndex ? 'bg-blue-500' : 'bg-slate-700'
                )}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default StatusTimeline;
